/**
 * YNS Supabase Client
 * Shared Supabase configuration and utilities for cross-device data sync.
 *
 * Features:
 * - Centralized Supabase configuration
 * - Generic upsert/select operations with customer_id filtering
 * - Hybrid sync strategy: localStorage first, Supabase async
 * - Offline detection and queue management
 * - Conflict resolution (server wins if more recent, preserve highest progress)
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const SUPABASE_CONFIG = window.YNS_SUPABASE_CONFIG || {
    url: '',
    anonKey: ''
  };

  // Merge in table names and settings (these are internal, not from window config)
  SUPABASE_CONFIG.tables = SUPABASE_CONFIG.tables || {
    videoProgress: 'yns_locksmith_video_progress',
    streaks: 'yns_customer_streaks',
    achievements: 'yns_customer_achievements',
    pdfAnnotations: 'yns_pdf_annotations',
    pdfProgress: 'yns_pdf_progress',
    studySessions: 'yns_study_sessions',
    studyNotes: 'yns_study_notes',
    activityLog: 'yns_activity_log'
  };

  // Sync settings
  SUPABASE_CONFIG.syncDebounceMs = SUPABASE_CONFIG.syncDebounceMs || 2000;
  SUPABASE_CONFIG.maxRetries = SUPABASE_CONFIG.maxRetries || 3;
  SUPABASE_CONFIG.retryDelayMs = SUPABASE_CONFIG.retryDelayMs || 1000;

  // ============================================
  // State
  // ============================================
  let syncQueue = [];
  let isSyncing = false;
  let syncTimeouts = {};
  let isOnline = navigator.onLine;

  // ============================================
  // Network Status
  // ============================================
  window.addEventListener('online', () => {
    isOnline = true;
    processSyncQueue();
  });

  window.addEventListener('offline', () => {
    isOnline = false;
  });

  // ============================================
  // Core API Functions
  // ============================================

  /**
   * Get customer ID from YNS_CONFIG
   */
  function getCustomerId() {
    const config = window.YNS_CONFIG || {};
    return config.customerId ? String(config.customerId) : null;
  }

  /**
   * Make authenticated request to Supabase REST API
   */
  async function supabaseRequest(endpoint, options = {}) {
    const url = `${SUPABASE_CONFIG.url}/rest/v1/${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_CONFIG.anonKey,
      'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Supabase error ${response.status}: ${errorText}`);
      }

      // Handle empty responses (e.g., for upserts with Prefer: return=minimal)
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error('[Supabase] Request failed:', error);
      throw error;
    }
  }

  /**
   * SELECT rows from a table with filters
   */
  async function select(table, filters = {}, options = {}) {
    const customerId = getCustomerId();
    if (!customerId) {
      console.warn('[Supabase] No customer ID, skipping select');
      return [];
    }

    // Build query string
    let queryParts = [`customer_id=eq.${encodeURIComponent(customerId)}`];

    // Add additional filters
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        queryParts.push(`${key}=eq.${encodeURIComponent(value)}`);
      }
    }

    // Add select columns if specified
    const selectColumns = options.select || '*';
    queryParts.push(`select=${selectColumns}`);

    // Add ordering if specified
    if (options.orderBy) {
      const direction = options.orderDesc ? 'desc' : 'asc';
      queryParts.push(`order=${options.orderBy}.${direction}`);
    }

    // Add limit if specified
    if (options.limit) {
      queryParts.push(`limit=${options.limit}`);
    }

    const queryString = queryParts.join('&');

    return await supabaseRequest(`${table}?${queryString}`, {
      method: 'GET'
    });
  }

  /**
   * UPSERT (insert or update) a row
   */
  async function upsert(table, data, conflictColumns = ['customer_id']) {
    const customerId = getCustomerId();
    if (!customerId) {
      console.warn('[Supabase] No customer ID, skipping upsert');
      return null;
    }

    // Ensure customer_id is set
    const rowData = {
      ...data,
      customer_id: customerId,
      updated_at: new Date().toISOString()
    };

    const conflictParam = conflictColumns.join(',');

    return await supabaseRequest(`${table}?on_conflict=${conflictParam}`, {
      method: 'POST',
      headers: {
        'Prefer': 'resolution=merge-duplicates,return=minimal'
      },
      body: JSON.stringify(rowData)
    });
  }

  /**
   * UPSERT multiple rows in a batch
   */
  async function upsertBatch(table, rows, conflictColumns = ['customer_id']) {
    const customerId = getCustomerId();
    if (!customerId || rows.length === 0) {
      return null;
    }

    // Ensure customer_id and updated_at on all rows
    const timestamp = new Date().toISOString();
    const rowsData = rows.map(row => ({
      ...row,
      customer_id: customerId,
      updated_at: timestamp
    }));

    const conflictParam = conflictColumns.join(',');

    return await supabaseRequest(`${table}?on_conflict=${conflictParam}`, {
      method: 'POST',
      headers: {
        'Prefer': 'resolution=merge-duplicates,return=minimal'
      },
      body: JSON.stringify(rowsData)
    });
  }

  /**
   * INSERT a new row (without upsert)
   */
  async function insert(table, data) {
    const customerId = getCustomerId();
    if (!customerId) {
      console.warn('[Supabase] No customer ID, skipping insert');
      return null;
    }

    const rowData = {
      ...data,
      customer_id: customerId,
      created_at: new Date().toISOString()
    };

    return await supabaseRequest(table, {
      method: 'POST',
      headers: {
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(rowData)
    });
  }

  /**
   * DELETE rows matching filters
   */
  async function deleteRows(table, filters = {}) {
    const customerId = getCustomerId();
    if (!customerId) {
      console.warn('[Supabase] No customer ID, skipping delete');
      return null;
    }

    // Build query string
    let queryParts = [`customer_id=eq.${encodeURIComponent(customerId)}`];

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        queryParts.push(`${key}=eq.${encodeURIComponent(value)}`);
      }
    }

    const queryString = queryParts.join('&');

    return await supabaseRequest(`${table}?${queryString}`, {
      method: 'DELETE'
    });
  }

  // ============================================
  // Sync Queue Management
  // ============================================

  /**
   * Add a sync operation to the queue (debounced)
   */
  function queueSync(key, syncFn, debounceMs = SUPABASE_CONFIG.syncDebounceMs) {
    // Clear existing timeout for this key
    if (syncTimeouts[key]) {
      clearTimeout(syncTimeouts[key]);
    }

    // Set new debounced timeout
    syncTimeouts[key] = setTimeout(() => {
      delete syncTimeouts[key];

      if (isOnline) {
        executeSyncWithRetry(key, syncFn);
      } else {
        // Queue for later when online
        syncQueue.push({ key, syncFn, timestamp: Date.now() });
      }
    }, debounceMs);
  }

  /**
   * Execute sync with retry logic
   */
  async function executeSyncWithRetry(key, syncFn, attempt = 1) {
    try {
      await syncFn();
    } catch (error) {
      console.error(`[Supabase] Sync failed (attempt ${attempt}):`, key, error);

      if (attempt < SUPABASE_CONFIG.maxRetries) {
        const delay = SUPABASE_CONFIG.retryDelayMs * attempt;
        setTimeout(() => {
          executeSyncWithRetry(key, syncFn, attempt + 1);
        }, delay);
      } else {
        // Queue for later retry
        syncQueue.push({ key, syncFn, timestamp: Date.now() });
      }
    }
  }

  /**
   * Process queued sync operations
   */
  async function processSyncQueue() {
    if (isSyncing || syncQueue.length === 0 || !isOnline) return;

    isSyncing = true;

    while (syncQueue.length > 0 && isOnline) {
      const item = syncQueue.shift();
      try {
        await item.syncFn();
      } catch (error) {
        console.error('[Supabase] Queued sync failed:', item.key, error);
        // Don't re-queue to avoid infinite loops
      }
    }

    isSyncing = false;
  }

  // ============================================
  // Hybrid Sync Utilities
  // ============================================

  /**
   * Sync data with localStorage fallback
   * - Writes to localStorage immediately
   * - Queues Supabase sync (debounced)
   * - Returns merged data on load
   */
  async function syncWithFallback(options) {
    const {
      table,
      localKey,
      data,
      conflictColumns = ['customer_id'],
      mergeStrategy = 'serverWins'
    } = options;

    // Save to localStorage immediately
    try {
      localStorage.setItem(localKey, JSON.stringify(data));
    } catch (e) {
      console.error('[Supabase] localStorage save failed:', e);
    }

    // Queue Supabase sync
    queueSync(`${table}:${localKey}`, async () => {
      await upsert(table, data, conflictColumns);
    });
  }

  /**
   * Load and merge data from localStorage and Supabase
   */
  async function loadWithMerge(options) {
    const {
      table,
      localKey,
      filters = {},
      mergeStrategy = 'serverWins',
      transformServerData = (d) => d,
      transformLocalData = (d) => d
    } = options;

    // Load from localStorage first (instant)
    let localData = null;
    try {
      const stored = localStorage.getItem(localKey);
      localData = stored ? transformLocalData(JSON.parse(stored)) : null;
    } catch (e) {
      console.error('[Supabase] localStorage load failed:', e);
    }

    // If offline, return local data only
    if (!isOnline) {
      return localData;
    }

    // Fetch from server
    try {
      const serverRows = await select(table, filters);
      const serverData = serverRows.length > 0 ? transformServerData(serverRows) : null;

      // Merge based on strategy
      const merged = mergeData(localData, serverData, mergeStrategy);

      // Update localStorage with merged data
      if (merged) {
        localStorage.setItem(localKey, JSON.stringify(merged));
      }

      return merged;
    } catch (error) {
      console.error('[Supabase] Server fetch failed, using local:', error);
      return localData;
    }
  }

  /**
   * Merge local and server data based on strategy
   */
  function mergeData(local, server, strategy) {
    // If one is null, return the other
    if (!local && !server) return null;
    if (!local) return server;
    if (!server) return local;

    switch (strategy) {
      case 'serverWins':
        // Server data takes precedence
        return server;

      case 'localWins':
        // Local data takes precedence
        return local;

      case 'newest':
        // Compare updated_at timestamps
        const localTime = local.updated_at ? new Date(local.updated_at).getTime() : 0;
        const serverTime = server.updated_at ? new Date(server.updated_at).getTime() : 0;
        return serverTime >= localTime ? server : local;

      case 'highestProgress':
        // For progress data, keep highest values
        return mergeHighestProgress(local, server);

      case 'mergeArrays':
        // For arrays, combine and dedupe by ID
        return mergeArraysById(local, server);

      default:
        return server;
    }
  }

  /**
   * Merge keeping highest progress values
   */
  function mergeHighestProgress(local, server) {
    const merged = { ...local };

    for (const [key, value] of Object.entries(server)) {
      if (typeof value === 'number' && typeof merged[key] === 'number') {
        merged[key] = Math.max(merged[key], value);
      } else if (typeof value === 'boolean' && value === true) {
        merged[key] = true; // Completed always wins
      } else if (key === 'updated_at') {
        // Keep most recent timestamp
        const localTime = merged[key] ? new Date(merged[key]).getTime() : 0;
        const serverTime = value ? new Date(value).getTime() : 0;
        merged[key] = serverTime >= localTime ? value : merged[key];
      } else if (merged[key] === undefined || merged[key] === null) {
        merged[key] = value;
      }
    }

    return merged;
  }

  /**
   * Merge arrays by ID, keeping newest version of each item
   */
  function mergeArraysById(localArray, serverArray) {
    if (!Array.isArray(localArray)) localArray = [];
    if (!Array.isArray(serverArray)) serverArray = [];

    const merged = new Map();

    // Add local items
    for (const item of localArray) {
      const id = item.id || item.achievement_id || JSON.stringify(item);
      merged.set(id, item);
    }

    // Merge server items (server wins on conflict by timestamp)
    for (const item of serverArray) {
      const id = item.id || item.achievement_id || JSON.stringify(item);
      const existing = merged.get(id);

      if (!existing) {
        merged.set(id, item);
      } else {
        // Compare timestamps
        const existingTime = existing.created_at ? new Date(existing.created_at).getTime() : 0;
        const itemTime = item.created_at ? new Date(item.created_at).getTime() : 0;

        if (itemTime >= existingTime) {
          merged.set(id, item);
        }
      }
    }

    return Array.from(merged.values());
  }

  // ============================================
  // Sync Status Indicator
  // ============================================

  let syncStatusEl = null;

  function showSyncStatus(status) {
    if (!syncStatusEl) {
      syncStatusEl = document.createElement('div');
      syncStatusEl.className = 'yns-sync-status';
      document.body.appendChild(syncStatusEl);
    }

    const statusConfig = {
      syncing: { text: 'Syncing...', icon: '🔄', class: 'syncing' },
      synced: { text: 'Synced', icon: '✓', class: 'synced' },
      offline: { text: 'Offline', icon: '📴', class: 'offline' },
      error: { text: 'Sync failed', icon: '⚠️', class: 'error' }
    };

    const config = statusConfig[status] || statusConfig.synced;

    syncStatusEl.innerHTML = `<span class="yns-sync-icon">${config.icon}</span><span class="yns-sync-text">${config.text}</span>`;
    syncStatusEl.className = `yns-sync-status ${config.class} visible`;

    // Auto-hide after 2 seconds (except for offline/error)
    if (status === 'synced') {
      setTimeout(() => {
        syncStatusEl.classList.remove('visible');
      }, 2000);
    }
  }

  // ============================================
  // Initialization
  // ============================================

  function init() {
    // Add sync status styles
    const styles = document.createElement('style');
    styles.textContent = `
      .yns-sync-status {
        position: fixed;
        bottom: 20px;
        right: 20px;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.15);
        font-size: 0.8125rem;
        font-weight: 500;
        z-index: 99999;
        opacity: 0;
        transform: translateY(10px);
        transition: opacity 0.3s ease, transform 0.3s ease;
        pointer-events: none;
      }

      .yns-sync-status.visible {
        opacity: 1;
        transform: translateY(0);
      }

      .yns-sync-status.syncing {
        color: #2196F3;
      }

      .yns-sync-status.syncing .yns-sync-icon {
        animation: yns-spin 1s linear infinite;
      }

      .yns-sync-status.synced {
        color: #4CAF50;
      }

      .yns-sync-status.offline {
        color: #FF9800;
      }

      .yns-sync-status.error {
        color: #F44336;
      }

      @keyframes yns-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @media (max-width: 768px) {
        .yns-sync-status {
          bottom: 70px;
          right: 10px;
          font-size: 0.75rem;
          padding: 6px 10px;
        }
      }
    `;
    document.head.appendChild(styles);

    // Process any pending syncs on load
    if (isOnline) {
      setTimeout(processSyncQueue, 1000);
    }
  }

  // ============================================
  // Public API
  // ============================================
  window.YNS_SUPABASE = {
    // Config
    config: SUPABASE_CONFIG,
    tables: SUPABASE_CONFIG.tables,

    // Core operations
    select,
    upsert,
    upsertBatch,
    insert,
    delete: deleteRows,
    request: supabaseRequest,

    // Utilities
    getCustomerId,
    isOnline: () => isOnline,

    // Sync management
    queueSync,
    processSyncQueue,
    syncWithFallback,
    loadWithMerge,

    // Merge utilities
    mergeData,
    mergeHighestProgress,
    mergeArraysById,

    // Status
    showSyncStatus
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
